import moment from 'moment';
import { HdCycleKey } from '../bodygraph/hd-cycle-chart.models';
import { HD_CHIRON_DISPLAY_CONFIG, HD_PLANET_DISPLAY_CONFIGS } from '../bodygraph/planet-grid/hd-planet-display-config';

/**
 * Iconen van de twee Just Now-varianten, overal dezelfde (Cycles-sectie, chart
 * manager, shortcut-bar): `full` = volledige chart van nu (design + personality),
 * zelfde stopwatch als het chart-type Just Now; `transit` = enkel de
 * transiterende planeten van nu (personality-kant). Bewust anders dan
 * `clock-rotate-left` van de Just Now-transit-overlay op de chart.
 */
export const HD_JUST_NOW_ICONS = {
  full: 'stopwatch',
  transit: 'clock',
} as const;

/**
 * Rave New Year (de zon gaat poort 41 binnen): snelle actie in de chart manager
 * en de badge in de transits-dag-flow. De varianten Volledig/Transit gebruiken
 * de Just Now-iconen hierboven.
 */
export const HD_RAVE_NEW_YEAR_ICON = 'calendar-star';

/** FA7-iconen per cycle (bare namen; gerenderd via raw fa-klassen in de sectie). */
export const HD_CYCLE_ICONS: Readonly<Record<HdCycleKey, string>> = {
  saturnReturn: 'planet-ringed',
  secondSaturnReturn: 'planet-ringed',
  uranusOpposition: 'circle-half-stroke',
  chironReturn: 'meteor',
  // Volle cirkel tegenover de halve van de oppositie: één volledige omloop.
  uranusReturn: 'circle',
  // Jupiter = de wet (en bescherming): terugkerend om de ±12 jaar.
  jupiterReturn: 'scale-balanced',
  solarReturn: 'sun',
  justNow: HD_JUST_NOW_ICONS.full,
};

const astronomiconCharFor = (key: 'sun' | 'jupiter' | 'saturn' | 'uranus'): string =>
  HD_PLANET_DISPLAY_CONFIGS.find(config => config.key === key)?.astronomiconChar ?? '';

/** Astronomicon-glyph van de cyclusplaneet; null = geen glyph (Just now). */
export const HD_CYCLE_GLYPHS: Readonly<Record<HdCycleKey, string | null>> = {
  saturnReturn: astronomiconCharFor('saturn'),
  secondSaturnReturn: astronomiconCharFor('saturn'),
  uranusOpposition: astronomiconCharFor('uranus'),
  chironReturn: HD_CHIRON_DISPLAY_CONFIG.astronomiconChar,
  uranusReturn: astronomiconCharFor('uranus'),
  jupiterReturn: astronomiconCharFor('jupiter'),
  solarReturn: astronomiconCharFor('sun'),
  justNow: null,
};

/** i18n-label van een cycle-key; onbekende keys vallen terug op de rauwe string. */
export function hdCycleLabel(translate: (key: string) => string, cycleKey: string | null | undefined): string {
  if (!cycleKey) return '—';
  const key = `humanDesign.cycles.types.${cycleKey}`;
  const label = translate(key);
  return label === key ? cycleKey : label;
}

/** Jaartal van het cyclusmoment; Just now krijgt de volledige datum (uniek per moment). */
function cycleMomentSuffix(cycleKey: HdCycleKey, anchorUtc: string, solarReturnYear?: number): string {
  const anchor = moment.utc(anchorUtc);
  return cycleKey === 'justNow'
    ? anchor.local().format('D MMM YYYY')
    : `${solarReturnYear ?? anchor.year()}`;
}

/** "Saturnus-return — Sophie (2044)"; Just now krijgt de volledige datum (uniek per moment). */
export function buildCycleChartName(
  translate: (key: string) => string,
  cycleKey: HdCycleKey,
  natalName: string,
  anchorUtc: string,
  solarReturnYear?: number,
): string {
  const label = hdCycleLabel(translate, cycleKey);
  return `${label} — ${natalName} (${cycleMomentSuffix(cycleKey, anchorUtc, solarReturnYear)})`;
}

/**
 * Label van het cyclusmoment zónder de natal-naam — "Saturnus-return (2044)" —
 * voor plekken waar de persoon al apart benoemd staat (bv. het drieluik op
 * een cycle-tab: persoon links, cyclus rechts).
 */
export function buildCycleMomentLabel(
  translate: (key: string) => string,
  cycleKey: HdCycleKey,
  anchorUtc: string,
  solarReturnYear?: number,
): string {
  const label = hdCycleLabel(translate, cycleKey);
  return `${label} (${cycleMomentSuffix(cycleKey, anchorUtc, solarReturnYear)})`;
}
